import type { Parchment } from 'quill';
import React, { useState } from 'react';
import InputTabs from '../../../components/InputTabs';

type EditingTarget = 'link' | 'text';

const unsavedLink = new WeakMap<Parchment.Blot, string>();
const unsavedText = new WeakMap<Parchment.Blot, string>();

interface LinkEditorProps {
  blot: Parchment.Blot;
  initialLink: string;
  initialText: string;
  textPlaceholder?: string;
  linkPlaceholder?: string;
  editingTarget: EditingTarget;
  onEditingTargetChange: (target: EditingTarget | null) => void;
  onSave: (value: string) => void;
}

const LinkEditor = ({
  blot,
  initialLink,
  initialText,
  textPlaceholder,
  linkPlaceholder,
  editingTarget,
  onEditingTargetChange,
  onSave,
}: LinkEditorProps) => {
  const [link, setLink] = useState(unsavedLink.get(blot) || initialLink);
  const [text, setText] = useState(unsavedText.get(blot) || initialText);

  const handleSave = () => {
    if (editingTarget === 'text') {
      onSave(text);
      unsavedText.delete(blot);
    } else {
      onSave(link);
      unsavedLink.delete(blot);
    }
    onEditingTargetChange(null);
  };

  return (
    <InputTabs.Root>
      <InputTabs.List>
        <InputTabs.TabButton
          active={editingTarget === 'link'}
          onClick={() => onEditingTargetChange('link')}
          aria-label="Edit link"
        >
          Link
        </InputTabs.TabButton>
        <InputTabs.TabButton
          active={editingTarget === 'text'}
          onClick={() => onEditingTargetChange('text')}
          aria-label="Edit text"
        >
          Text
        </InputTabs.TabButton>
      </InputTabs.List>
      <InputTabs.Input
        onSave={handleSave}
        onEscape={() => onEditingTargetChange(null)}
        placeholder={
          editingTarget === 'text' ? textPlaceholder : linkPlaceholder
        }
        value={editingTarget === 'text' ? text : link}
        onChange={(value) => {
          if (editingTarget === 'text') {
            unsavedText.set(blot, value);
            setText(value);
          } else {
            unsavedLink.set(blot, value);
            setLink(value);
          }
        }}
        saveDisabled={
          editingTarget === 'link'
            ? !link || link === initialLink
            : text === initialText
        }
        aria-label={editingTarget === 'text' ? 'Text' : 'Link'}
      />
    </InputTabs.Root>
  );
};

export default LinkEditor;
